import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateWaiver } from '../../../hooks/waivers/useCreateWaiver';
import { useUpdateWaiver } from '../../../hooks/waivers/useUpdateWaiver';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import QuillEditor from '../../common/forms/QuillEditor';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import WaiverPreview from '../modals/WaiverPreview';
import WaiverTemplateSelect from '../controls/WaiverTemplateSelect';
import { replaceTags } from '../../../utils/replaceTags';
import { getCurrentDate } from '../../../utils/getCurrentDate';
import { getPetById } from '../../../api/petService';
import ConfirmDialog from '../../common/modals/ConfirmDialog';

export default function WaiverForm({ successCallback = null, pet = null, history = null, historyEntry = null, waiver = null, open, onClose }) {

    const { t } = useTranslation('common'); 

    const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

    const quillModules = {
      toolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        // Notice: no 'link' here
        ['clean']
      ]
    };

    const [waiverData, setWaiverData] = React.useState(waiver);
    const [name, setName] = React.useState(waiver ? waiver.name : "");
    const [content, setContent] = React.useState(waiver ? waiver.content : "");

    const { create } = useCreateWaiver(successCallback);
    const { update } = useUpdateWaiver(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(waiver ? waiver.name : "");
        setContent(waiver ? waiver.content : "");
        setWaiverData(waiver);
    }, [waiver, open]);

    const [previewOpen, setPreviewOpen] = React.useState(false);

    const [tags, setTags] = React.useState(null);

    React.useEffect(() => {
      if (open) {
        const fetchPetData = async () => {
          const petData = await getPetById(pet.id);
          setTags({
            petName: petData.name,
            petSpecies: petData.species ? petData.species : petData.speciesLookup.label,
            petBreed: petData.breed ? petData.breed : "",
            petOwnerName: `${petData.owner.first_name} ${petData.owner.last_name}`,
            clinicName: petData.owner.office.name,
            currentDate: getCurrentDate()
          });
        };

        fetchPetData();
      }
    }, [open, pet.id]);


    const onWaiverTemplateSelect = (object) => {
      setName(object.name);
      setContent(replaceTags(object.content, tags));
    };

    const handlePreviewClick = () => {
      setPreviewOpen(true);
    };  

    const onCloseButtonClick= () => {
      if (waiverData) {
        if (waiverData.name !== name || waiverData.content !== content)
        {
          setConfirmDialogOpen(true);
        } else {
          handleCancel();
        }
      } else {
        if (name || content !== "<p></p>") {
          setConfirmDialogOpen(true);
        } else {
          handleCancel();
        }
      }
      
    };

    const onConfirmCloseYes = () => {
      handleCancel();
      setConfirmDialogOpen(false);
    };

    const onConfirmCloseNo = () => {
      setConfirmDialogOpen(false);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            showSnackbar(t('waivers.messages.name_required'), "warning");
            return;
        }
        if (!content.trim()) {
            showSnackbar(t('waivers.messages.content_required'), "warning");
            return;
        }

        try 
        {
            if (!waiverData) {
                const newRecord = await create({ 
                    pet_id: pet ? pet.id : null, 
                    pet_owner_id: pet ? pet.owner.id : null, 
                    history_id: history ? history.id : null,
                    history_entry_id: historyEntry ? historyEntry.id : null,
                    name: name,
                    content: content
                });
                setWaiverData(newRecord);
                showSnackbar(t('waivers.messages.create_success'), "success");
            } else {
                await update(waiverData.id, { 
                    name: name,
                    content: content
                });
                showSnackbar(t('waivers.messages.update_success'), "success");
            }
        } catch (err) {
            showSnackbar(t('waivers.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
        setName("");
        setContent("");
        setWaiverData(null);
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{"Waiver"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
              <WaiverTemplateSelect 
                onSelect={onWaiverTemplateSelect}
              />
              <TextField
                label="Waiver Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
              />
              <QuillEditor
                modules={quillModules}
                value={content}
                onChange={setContent} 
                showTagsDropdown={false}
              />
            </Stack>
        </DialogContent>

        <DialogActions>
          <Button disabled={!waiverData || waiverData.content !== content} onClick={handlePreviewClick}>
              <FindInPageIcon />
          </Button>
          <div style={{flex: '1 0 0'}} />
          <Button disabled={!name && content==="<p></p>"} onClick={handleSave}>
              Save
          </Button>
          <Button onClick={onCloseButtonClick}>Close</Button>
        </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={confirmDialogOpen}
          title={t('waivers.messages.you_have_unsaved')}
          content={t('waivers.messages.you_have_unsaved_question')}
          confirmText="Close without saving"
          cancelText="Cancel"
          onConfirm={onConfirmCloseYes}
          onCancel={onConfirmCloseNo}
        />

        <WaiverPreview 
          waiver={waiverData}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
        </>
    );
}